/**
 * The Leaders panel's pure half (`docs/plans/leader-screener.md`, step 5; mock
 * `docs/leader-screener-mock.html`).
 *
 * The panel sits over **any** screen. It decides which of the screen's rows are
 * shown — Growth Rank (`ergRank`) at or above a threshold, in the box(es) picked — and joins
 * its own columns onto them, **as of its date**. Every other column is the
 * screen's own, today. Nothing here fetches or renders.
 */

import type { ScreenerColumn, ScreenerConfig } from "@qufolio/model";
import type { ScreenerFrame, ScreenerRow } from "@qufolio/transport";

import type { LeaderPrior, LeaderRow, LeadersAnswer } from "../api/leaders";

// ── the grid of boxes ────────────────────────────────────────────────────────

/** Boxes 1–9 as the 3×3 grid, row by row; 10 above 0 in the side column. */
export const GRID_ROWS: readonly (readonly number[])[] = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
];
export const SIDE_BOXES: readonly number[] = [10, 0];
/** Every box, in the order the pie and its legend read. */
export const BOX_ORDER: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 0];

export function boxLabel(box: number): string {
  return box === 0 ? "No Box" : `Box ${box}`;
}

// ── ERG ──────────────────────────────────────────────────────────────────────

/** The owner's default: 240 or more is a strong stock (2026-11-02). */
export const DEFAULT_THRESHOLD = 240;
/** ERG runs 0–297: three 1–99 ranks. */
export const ERG_MAX = 297;

/** The owner's colours: 240–297 green, 180–239 yellow, 3–179 red; 0 is none. */
export type ErgBand = "strong" | "middle" | "weak" | "none";
export function ergBand(erg: number | null | undefined): ErgBand {
  if (erg == null || erg <= 0) return "none";
  if (erg >= 240) return "strong";
  if (erg >= 180) return "middle";
  return "weak";
}

export function clampThreshold(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_THRESHOLD;
  return Math.min(ERG_MAX, Math.max(0, Math.round(value)));
}

/** At or above the threshold — and never ERG 0, which means a leg is missing. */
export function qualifies(erg: number | null | undefined, threshold: number): boolean {
  return erg != null && erg > 0 && erg >= threshold;
}

// ── the leaders, their positions, and the session before ────────────────────

export interface Leader extends LeaderRow {
  /** 1-based position among the day's qualifying rows, by ERG. */
  position: number;
  /** Positions moved since the prior session (+ up); null when it was not a leader then. */
  delta: number | null;
  isNew: boolean;
}

function ranked<T extends { symbol: string; ergRank: number | null }>(rows: readonly T[], threshold: number): T[] {
  return rows
    .filter((r) => qualifies(r.ergRank, threshold))
    .sort((a, b) => (b.ergRank ?? 0) - (a.ergRank ?? 0) || a.symbol.localeCompare(b.symbol));
}

export interface LeadersView {
  leaders: Leader[];
  /** Symbols that were leaders at the prior session and are not now. */
  left: string[];
  entered: number;
  hasPrior: boolean;
}

export function leadersOf(answer: LeadersAnswer, threshold: number): LeadersView {
  const now = ranked(answer.rows, threshold);
  const before = ranked<LeaderPrior>(answer.prior, threshold);
  const hasPrior = answer.prior.length > 0;
  const priorPos = new Map(before.map((r, i) => [r.symbol, i + 1]));
  const nowSet = new Set(now.map((r) => r.symbol));
  const leaders = now.map((r, i) => {
    const was = priorPos.get(r.symbol);
    return { ...r, position: i + 1, delta: was === undefined ? null : was - (i + 1), isNew: hasPrior && was === undefined };
  });
  return {
    leaders,
    left: before.filter((r) => !nowSet.has(r.symbol)).map((r) => r.symbol),
    entered: leaders.filter((l) => l.isNew).length,
    hasPrior,
  };
}

export interface BoxStat {
  count: number;
  /** Mean ERG of the box's leaders, rounded; null when empty. */
  avgErg: number | null;
}

/** Count and average ERG per box. A leader with no box (no data) is in none. */
export function boxStats(leaders: readonly Leader[]): Map<number, BoxStat> {
  const out = new Map<number, BoxStat>(BOX_ORDER.map((b) => [b, { count: 0, avgErg: null }]));
  const sums = new Map<number, number>();
  for (const l of leaders) {
    if (l.growthBox == null) continue;
    const b = Math.round(l.growthBox);
    const stat = out.get(b);
    if (!stat) continue;
    stat.count += 1;
    sums.set(b, (sums.get(b) ?? 0) + (l.ergRank ?? 0));
  }
  for (const [b, stat] of out) {
    stat.avgErg = stat.count ? Math.round((sums.get(b) ?? 0) / stat.count) : null;
  }
  return out;
}

// ── the selection ───────────────────────────────────────────────────────────

/**
 * A plain click selects one box, and clicking the sole selection clears it;
 * with a modifier (shift / ctrl / ⌘) it toggles that box in or out.
 */
export function pickBox(selection: ReadonlySet<number>, box: number, additive: boolean): Set<number> {
  if (additive) {
    const next = new Set(selection);
    if (next.has(box)) next.delete(box);
    else next.add(box);
    return next;
  }
  return selection.size === 1 && selection.has(box) ? new Set() : new Set([box]);
}

export function inSelection(leader: Leader, selection: ReadonlySet<number>): boolean {
  if (selection.size === 0) return true;
  return leader.growthBox != null && selection.has(Math.round(leader.growthBox));
}

// ── onto the screen's rows ──────────────────────────────────────────────────

/** The panel's columns, named so none can collide with a screen's own field. */
export const FIELDS = {
  last: "ldLast",
  chg: "ldChg",
  erg: "ldErg",
  box: "ldBox",
  eps: "ldEps",
  rs: "ldRs",
  grp: "ldGrp",
  since: "ldSince",
} as const;

const PANEL_FIELD_SET = new Set<string>(Object.values(FIELDS));
export function isPanelField(field: string): boolean {
  return PANEL_FIELD_SET.has(field);
}

/** `2026-03-02` → `3/2`, for a header badge. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : iso;
}

/**
 * The panel's columns, first in the grid. On a past date their headers carry the
 * date (the "as of D" badge) and Since date % appears; on Latest neither does.
 */
export function panelColumns(pastDate: string | null): ScreenerColumn[] {
  // One header band over the panel's columns: it names the date they are as of,
  // which is the "as of D" badge; on Latest it just says whose columns they are.
  const band = pastDate ? `Boxes · as of ${shortDate(pastDate)}` : "Boxes";
  const cols: ScreenerColumn[] = [
    // Today's, whatever the date: the price on screen beside the as-of-D ranks
    // (owner, 2026-11-04, in place of # and Δ — # was only the GR order).
    { field: FIELDS.last, header: "Last", width: 70, precision: 2, band },
    { field: FIELDS.chg, header: "Chg %", width: 62, precision: 2, band, format: DELTA_FORMAT },
    { field: FIELDS.erg, header: "GR", width: 50, precision: 0, band, format: ERG_FORMAT },
    { field: FIELDS.box, header: "Box", width: 52, precision: 0, band },
    { field: FIELDS.eps, header: "EPS Rank", width: 70, precision: 0, band, format: RANK_FORMAT },
    { field: FIELDS.rs, header: "RS Rank I", width: 72, precision: 0, band },
    { field: FIELDS.grp, header: "Industry Rank", width: 92, precision: 0, band },
  ];
  if (pastDate) {
    cols.push({ field: FIELDS.since, header: "Since date %", width: 92, precision: 1, band, format: DELTA_FORMAT });
  }
  return cols;
}

/** The owner's ERG colours (2026-11-02): 240–297 green, 180–239 yellow, 3–179 red. */
const ERG_FORMAT: ScreenerColumn["format"] = [
  { when: { op: "gte", value: 240 }, style: { fg: "positive", bold: true } },
  { when: { op: "between", value: 180, valueTo: 239 }, style: { fg: "warn" } },
  { when: { op: "between", value: 3, valueTo: 179 }, style: { fg: "negative" } },
];
/** The source's EPS Rk colours: 80–99 green, 60–79 yellow, 1–59 red. */
const RANK_FORMAT: ScreenerColumn["format"] = [
  { when: { op: "gte", value: 80 }, style: { fg: "positive" } },
  { when: { op: "between", value: 60, valueTo: 79 }, style: { fg: "warn" } },
  { when: { op: "between", value: 1, valueTo: 59 }, style: { fg: "negative" } },
];
const DELTA_FORMAT: ScreenerColumn["format"] = [
  { when: { op: "gt", value: 0 }, style: { fg: "positive" } },
  { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
];

/** The grid's config with the panel's columns placed after the identity block. */
export function withPanelColumns(config: ScreenerConfig, pastDate: string | null): ScreenerConfig {
  const own = (config.columns ?? []).filter((c) => !isPanelField(c.field));
  const identity = own.findIndex((c) => c.field === "symbol");
  const at = identity >= 0 ? identity + 1 : 0;
  const panel = panelColumns(pastDate);
  return { ...config, columns: [...own.slice(0, at), ...panel, ...own.slice(at)] };
}

/**
 * The detail frame narrowed to the panel's leaders (in the selection) with the
 * panel's columns joined on by symbol. Other frames pass through. A leader the
 * grid does not hold — not a member today — is skipped: the screen is the universe.
 */
export function applyPanel(
  frames: readonly ScreenerFrame[],
  leaders: readonly Leader[],
  selection: ReadonlySet<number>,
  pastDate: string | null,
): ScreenerFrame[] {
  const by = new Map(leaders.filter((l) => inSelection(l, selection)).map((l) => [l.symbol, l]));
  return frames.map((frame) => {
    if (frame.name !== "detail") return frame;
    const rows: ScreenerRow[] = [];
    for (const row of frame.rows) {
      const leader = by.get(String(row.symbol ?? ""));
      if (!leader) continue;
      const close = typeof row.close === "number" ? row.close : null;
      const chg = typeof row.changePercent === "number" ? row.changePercent : null;
      const then = leader.closeAsOf ?? null;
      rows.push({
        ...row,
        [FIELDS.last]: close,
        [FIELDS.chg]: chg,
        [FIELDS.erg]: leader.ergRank,
        [FIELDS.box]: leader.growthBox,
        [FIELDS.eps]: leader.epsRank,
        [FIELDS.rs]: leader.rsRankI,
        [FIELDS.grp]: leader.grpRank,
        ...(pastDate
          ? { [FIELDS.since]: close !== null && then ? (close / then - 1) * 100 : null }
          : {}),
      });
    }
    const added = Object.values(FIELDS)
      .filter((f) => pastDate || f !== FIELDS.since)
      .filter((f) => !frame.fields.some((x) => x.name === f))
      .map((name) => ({ name, type: "number" as const }));
    return { ...frame, fields: [...frame.fields, ...added], rows };
  });
}

// ── the pie ─────────────────────────────────────────────────────────────────

export interface Slice {
  box: number;
  count: number;
  /** SVG path of the ring segment, centred on (cx, cy). */
  d: string;
}

/** Ring segments for the non-empty boxes, clockwise from 12 o'clock. */
export function pieSlices(stats: Map<number, BoxStat>, cx: number, cy: number, outer: number, inner: number): Slice[] {
  const total = BOX_ORDER.reduce((s, b) => s + (stats.get(b)?.count ?? 0), 0);
  if (total === 0) return [];
  const p = (a: number, r: number) => `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  const out: Slice[] = [];
  let a0 = -Math.PI / 2;
  for (const box of BOX_ORDER) {
    const count = stats.get(box)?.count ?? 0;
    if (!count) continue;
    if (count === total) {
      // A full ring is two half arcs each way: one arc of 2π draws nothing.
      const d =
        `M${p(0, outer)} A${outer},${outer} 0 1 1 ${p(Math.PI, outer)} A${outer},${outer} 0 1 1 ${p(0, outer)} ` +
        `M${p(0, inner)} A${inner},${inner} 0 1 0 ${p(Math.PI, inner)} A${inner},${inner} 0 1 0 ${p(0, inner)} Z`;
      return [{ box, count, d }];
    }
    const a1 = a0 + (count / total) * 2 * Math.PI;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    out.push({
      box,
      count,
      d: `M${p(a0, outer)} A${outer},${outer} 0 ${big} 1 ${p(a1, outer)} L${p(a1, inner)} A${inner},${inner} 0 ${big} 0 ${p(a0, inner)} Z`,
    });
    a0 = a1;
  }
  return out;
}

// ── the date stepper ────────────────────────────────────────────────────────

/** The weekday before or after `iso`, skipping Saturday and Sunday. The server
 * snaps holidays to its own calendar; this only has to avoid asking for a weekend. */
export function stepWeekday(iso: string, dir: 1 | -1): string {
  const d = new Date(`${iso}T12:00:00Z`);
  do d.setUTCDate(d.getUTCDate() + dir);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}

// ── box colours (owner, 2026-11-03) ──────────────────────────────────────────

/** A box's fill and the text drawn on it. */
export interface BoxPaint {
  fill: string;
  text: string;
}

/** The dark text, and the green used when the theme's positive cannot be read. */
export const DARK_TEXT = "#0b0d10";
const FALLBACK_POSITIVE: Rgb = [63, 185, 80];
const GREY: Record<number, string> = { 10: "#b3bcc7", 0: "#d6dbe1" };

type Rgb = [number, number, number];

/** `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb(…)` or `rgba(…)` to RGB; anything else null. */
export function parseColour(css: string): Rgb | null {
  const s = css.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(s);
  if (hex) {
    const digits = hex[1] ?? "";
    const h = digits.length === 3 ? [...digits].map((c) => c + c).join("") : digits.slice(0, 6);
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(s);
  return rgb ? ([1, 2, 3].map((i) => Math.round(Number(rgb[i]))) as Rgb) : null;
}

function luminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** White or the dark text, whichever contrasts more with this fill. */
export function textOn(fill: Rgb): string {
  const l = luminance(fill);
  return (1.05) / (l + 0.05) > (l + 0.05) / (luminance([11, 13, 16]) + 0.05) ? "#ffffff" : DARK_TEXT;
}

/**
 * Box 1 the theme's positive, then lighter shades of it to box 9 (mixed with
 * white, 9 points a box); 10 and 0 light grey. The text is chosen per box: a
 * dark positive (a navy theme) needs white on its first boxes, where a light
 * one keeps dark text throughout.
 */
export function boxPaints(positive: string): (box: number) => BoxPaint {
  const base = parseColour(positive) ?? FALLBACK_POSITIVE;
  const out: Record<number, BoxPaint> = {};
  for (let box = 1; box <= 9; box++) {
    const p = (100 - (box - 1) * 9) / 100;
    const rgb = base.map((c) => Math.round(c * p + 255 * (1 - p))) as Rgb;
    out[box] = { fill: `rgb(${rgb.join(",")})`, text: textOn(rgb) };
  }
  const none: BoxPaint = { fill: GREY[0] ?? "#d6dbe1", text: DARK_TEXT };
  out[10] = { fill: GREY[10] ?? none.fill, text: DARK_TEXT };
  out[0] = none;
  return (box) => out[box] ?? none;
}
